/**
 * Routine Finder — Results Logic
 * Exposes: window.rfResults
 *   .CONCERN_LABELS                      — human-readable concern names (keyed by concern ID)
 *   .generateRoutineRecommendations(answers) → { hero, supporting[], routineHeadline }
 *
 * Each returned product object contains static catalogue data only.
 * Call rfShopify.enrichProducts([hero, ...supporting]) afterwards to add
 * live Shopify price, image, variantId, and availability.
 */
(function () {
  'use strict';

  // ─── Concern labels (shared with main controller) ─────────────────────────
  const CONCERN_LABELS = {
    fine_lines:        'Fine lines & Wrinkles',
    firmness:          'Loss of Firmness & Elasticity',
    congestion:        'Congestion or Hormonal Breakouts',
    hyperpigmentation: 'Hyperpigmentation or Dark Spots',
    acne_marks:        'Post-acne Marks',
    sun_damage:        'Sun Damage or Uneven Tone',
    texture:           'Rough or Uneven Texture',
    sensitivity:       'Sensitivity & Redness',
    enlarged_pores:    'Enlarged Pores',
    oily:              'Excess Oil',
    dehydrated:        'Dehydration',
    dull:              'Dullness or Lack of Radiance',
  };

  // ─── Product catalogue — derived from master registry (products-registry-routinefinder.js)
  const _reg = window.rfProducts || {};
  const PRODUCTS = Object.values(_reg)
    .filter(p => p.heroEligible)
    .map(p => ({
      id:         p.id,
      name:       p.name,
      price:      p.price,
      step:       p.step,
      stepName:   p.stepName,
      tagline:    p.tagline,
      keyBenefit: p.keyBenefit,
    }))
    .sort((a, b) => a.step - b.step);

  // ─── Scoring matrix ───────────────────────────────────────────────────────
  // Rank 1 = best fit · 2-4 = declining relevance · absent = not recommended
  // UAR = retinal_overnight_reform · FV = future_veil_peptide_cream
  // PPRS = peptide_pro-resilience_serum · CCA = complexion_accelerator
  // TPC = triphala_corrector
  const SCORES = {
    fine_lines:        { retinal_overnight_reform: 1, future_veil_peptide_cream: 2, 'peptide_pro-resilience_serum_concentrate_30ml': 3, saponins_cleanser: 4 },
    firmness:          { future_veil_peptide_cream: 1, 'peptide_pro-resilience_serum_concentrate_30ml': 2, retinal_overnight_reform: 3, saponins_cleanser: 4 },
    congestion:        { complexion_accelerator: 1, retinal_overnight_reform: 2, triphala_corrector: 3, saffron_cleanser: 4 },
    hyperpigmentation: { triphala_corrector: 1, retinal_overnight_reform: 2, complexion_accelerator: 3, saffron_cleanser: 4 },
    acne_marks:        { triphala_corrector: 1, retinal_overnight_reform: 2, complexion_accelerator: 3, saffron_cleanser: 4 },
    sun_damage:        { triphala_corrector: 1, retinal_overnight_reform: 2, complexion_accelerator: 3, saffron_cleanser: 4 },
    texture:           { complexion_accelerator: 1, retinal_overnight_reform: 2, 'peptide_pro-resilience_serum_concentrate_30ml': 3, saffron_cleanser: 4 },
    sensitivity:       { 'peptide_pro-resilience_serum_concentrate_30ml': 1, future_veil_peptide_cream: 2, saponins_cleanser: 3 },
    enlarged_pores:    { complexion_accelerator: 1, retinal_overnight_reform: 2, saffron_cleanser: 3 },
    oily:              { complexion_accelerator: 1, saffron_cleanser: 2 },
    dehydrated:        { 'peptide_pro-resilience_serum_concentrate_30ml': 1, future_veil_peptide_cream: 2, saponins_cleanser: 3 },
    dull:              { complexion_accelerator: 1, triphala_corrector: 2, 'peptide_pro-resilience_serum_concentrate_30ml': 3, saponins_cleanser: 4 },
  };

  // ─── Headline action phrases ──────────────────────────────────────────────
  const ROUTINE_ACTIONS = {
    fine_lines:        'smooth and diminish visible lines',
    firmness:          'firm and restore elasticity',
    congestion:        'decongest and clarify your complexion',
    hyperpigmentation: 'brighten and even your tone',
    acne_marks:        'fade marks and restore clarity',
    sun_damage:        'repair and protect against further damage',
    texture:           'resurface and refine texture',
    sensitivity:       'calm, strengthen, and protect',
    enlarged_pores:    'refine and minimise pores',
    oily:              'balance and control excess oil',
    dehydrated:        'deeply hydrate and plump',
    dull:              'illuminate and revive radiance',
  };

  // ─── Core logic ───────────────────────────────────────────────────────────
  function _score(concern, productId) {
    return SCORES[concern]?.[productId] ?? 0;
  }

  function generateRoutineRecommendations(answers) {
    const isOlder    = ['35-44', '45-54', '55+'].includes(answers.age_bracket);
    const sensitised = answers.sensitised === 'yes';
    const pregnant   = answers.pregnant   === 'yes';

    const excluded = new Set();
    if (!isOlder || pregnant || sensitised) excluded.add('retinal_overnight_reform');

    const primary   = answers.primary_concern   || '';
    const secondary = answers.secondary_concern || '';

    const eligible = PRODUCTS.filter(p => !excluded.has(p.id));

    // === Hero: deterministic — rank 1 product for primary concern ===
    // The customer's primary concern must always lead. Never blend it away.
    const primaryRankMap = SCORES[primary] || {};
    const heroPool = eligible
      .filter(p => p.stepName !== 'Cleanse' && primaryRankMap[p.id])
      .sort((a, b) => primaryRankMap[a.id] - primaryRankMap[b.id]);

    const fallback = PRODUCTS.find(p => p.id === 'future_veil_peptide_cream') || PRODUCTS[PRODUCTS.length - 1];
    if (!heroPool.length) {
      return { hero: { ...fallback }, supporting: [], routineHeadline: 'Your personalised Sachi Skin routine.' };
    }
    const hero = { ...heroPool[0], concernType: 'primary', concernKey: primary };

    // If hero also ranks 1–2 for the secondary concern, surface both icons on the card.
    const heroSecondaryScore = _score(secondary, hero.id);
    if (secondary && heroSecondaryScore > 0 && heroSecondaryScore <= 2) {
      hero.secondaryConcernKey = secondary;
    }

    // === Supporting: blended primary + secondary relevance ===
    // Products absent from a concern get rank penalty 5; lower total = more relevant.
    // Hard limits: exclude hero, max 1 cleanser, max 4 items total.
    const scored = eligible
      .filter(p => p.id !== hero.id)
      .map(p => {
        const ps = _score(primary, p.id);
        const ss = _score(secondary, p.id);
        if (ps === 0 && ss === 0) return null;
        // Determine which concern this product primarily serves.
        // When scores are tied, show secondary so both concerns appear in the routine.
        let concernType, concernKey;
        if (ps > 0 && (ss === 0 || ps < ss)) {
          concernType = 'primary'; concernKey = primary;
        } else if (ss > 0) {
          concernType = 'secondary'; concernKey = secondary;
        } else {
          concernType = 'primary'; concernKey = primary;
        }
        // concernScore: the rank of this product for the concern it's tagged with.
        // Used to suppress concern chips on cleansers that are only a weak (3/4) fit.
        const concernScore = (concernType === 'primary' ? ps : ss) || 0;
        return { product: p, total: (ps || 5) * 2 + (ss || 5), concernType, concernKey, concernScore };
      })
      .filter(Boolean)
      .sort((a, b) => a.total - b.total);

    const supporting = [];
    let cleanserCount = 0;
    for (const { product, concernType, concernKey, concernScore } of scored) {
      if (supporting.length >= 4) break;
      if (product.stepName === 'Cleanse') {
        if (cleanserCount >= 1) continue; // one cleanser in the routine is enough
        cleanserCount++;
      }
      supporting.push({ ...product, concernType, concernKey, concernScore });
    }

    // ── FV guarantee for 35+: always include Future Veil in the routine ──
    if (isOlder) {
      const fvId = 'future_veil_peptide_cream';
      const heroIsFV      = hero.id === fvId;
      const alreadyInList = supporting.some(p => p.id === fvId);
      if (!heroIsFV && !alreadyInList) {
        const fvProduct = PRODUCTS.find(p => p.id === fvId);
        if (fvProduct) {
          // Only show concern icon if FV ranks 1-2 for the user's primary or secondary concern
          const fvPrimary   = _score(primary, fvId);
          const fvSecondary = _score(secondary, fvId);
          let cType = '', cKey = '';
          if (fvPrimary > 0 && fvPrimary <= 2)        { cType = 'primary';   cKey = primary; }
          else if (fvSecondary > 0 && fvSecondary <= 2) { cType = 'secondary'; cKey = secondary; }
          supporting.push({
            ...fvProduct,
            concernType:  cType,
            concernKey:   cKey,
            concernScore: cType === 'primary' ? fvPrimary : (cType === 'secondary' ? fvSecondary : 0),
          });
        }
      }
    }

    supporting.sort((a, b) => a.step - b.step);

    const pLabel = (CONCERN_LABELS[primary]   || primary).toLowerCase();
    const sLabel = (CONCERN_LABELS[secondary] || secondary).toLowerCase();
    const action = ROUTINE_ACTIONS[primary] || 'transform your skin';
    const routineHeadline = `For ${pLabel} and ${sLabel}, this routine will ${action} while addressing ${sLabel}.`;

    return { hero, supporting, routineHeadline };
  }

  window.rfResults = { CONCERN_LABELS, generateRoutineRecommendations };
})();
